import { useState } from 'react';
import { Alert, Badge, Button, Group, Modal, Table, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useIsAdmin } from '../../../auth/permissions';
import { useActivateRecipe, useRecipesForStage } from '../api';
import { RecipeForm } from './RecipeForm';

function ActivateButton({ recipeId, stageId }: { recipeId: string; stageId: string }) {
  const isAdmin = useIsAdmin();
  const activate = useActivateRecipe(recipeId, stageId);
  return (
    <Tooltip label="Requires Admin role" disabled={isAdmin}>
      <span>
        <Button size="compact-xs" variant="light" disabled={!isAdmin} loading={activate.isPending} onClick={() => activate.mutate()}>
          Activate
        </Button>
      </span>
    </Tooltip>
  );
}

/** Recipe versions for one stage (BUILD_SPEC.md §5.5: "one active version per
 * stage (enforced)") — expanded inline under the owning stage row
 * (StagesRecipesConfig.tsx). */
export function RecipesPanel({ stageId }: { stageId: string }) {
  const [formOpened, setFormOpened] = useState(false);
  const recipesQuery = useRecipesForStage(stageId);
  const recipes = [...(recipesQuery.data?.items ?? [])].sort((a, b) => b.version - a.version);

  return (
    <>
      <Group justify="space-between" mb="xs">
        <Text fw={600} size="sm">
          Recipe versions
        </Text>
        <Button size="compact-xs" onClick={() => setFormOpened(true)}>
          New version
        </Button>
      </Group>
      {recipesQuery.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          Failed to load recipes.
        </Alert>
      )}
      <Table.ScrollContainer minWidth={640}>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Version</Table.Th>
              <Table.Th>Expected yield</Table.Th>
              <Table.Th>Effective from</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {recipes.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>v{r.version}</Table.Td>
                <Table.Td>{r.expectedYieldDryPct}%</Table.Td>
                <Table.Td>{new Date(r.effectiveFrom).toLocaleDateString()}</Table.Td>
                <Table.Td>
                  {r.isActive ? (
                    <Badge color="green">Active</Badge>
                  ) : (
                    <Badge color="gray" variant="light">
                      Inactive
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>{!r.isActive && <ActivateButton recipeId={r.id} stageId={stageId} />}</Table.Td>
              </Table.Tr>
            ))}
            {recipes.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" size="sm">
                    No recipe versions yet — create one before opening a run on this stage.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Modal opened={formOpened} onClose={() => setFormOpened(false)} title="New recipe version" size="xl">
        <RecipeForm stageId={stageId} onCreated={() => setFormOpened(false)} />
      </Modal>
    </>
  );
}
